import { Inject, Injectable } from '@angular/core';

import { LocalStorageService } from '../../shared/services/local-storage.service';
import { ServiceWorkerConfigService } from '../service-worker-config.service';
import { SERVICE_WORKER_ENABLED } from '../service-worker-enabled.injection-token';
import { SERVICE_WORKER_USER_ID_FACTORY } from '../service-worker-user-id.injection-token';
import { DiskPreloadMarkerName } from './disk-preload-marker-name.enum';
import { DISK_PRELOAD_REQUEST_MAP } from './disk-preload-request-map.injection-token';
import { DISK_PRELOAD_STORAGE_KEY } from './disk-preload-storage-key.injection-token';
import { DiskPreloadUrlPatternEntry } from './disk-preload-url-pattern-entry.model';
import { DISK_PRELOAD_URL_PATTERNS } from './disk-preload-url-patterns.injection-token';
import { DISK_PRELOAD_USER_ID_STORAGE_KEY } from './disk-preload-user-id-storage-key.injection-token';

interface DiskPreloadStorageEntry {
    readonly url: string;
    readonly markerName: DiskPreloadMarkerName;
}

@Injectable({
    providedIn: 'root'
})
export class DiskPreloadService {
    public readonly initialized: Promise<void>;
    public diskPreloadEnabled = true;

    private diskPreloadStorageEntries: ReadonlyArray<DiskPreloadStorageEntry> = [];

    constructor(
        private readonly localStorageService: LocalStorageService,
        @Inject(DISK_PRELOAD_USER_ID_STORAGE_KEY) private readonly userIdStorageKey: string,
        @Inject(DISK_PRELOAD_STORAGE_KEY) private readonly urlPatternStorageKey: string,
        @Inject(DISK_PRELOAD_URL_PATTERNS) private readonly urlPatternEntries: ReadonlyArray<DiskPreloadUrlPatternEntry>,
        @Inject(SERVICE_WORKER_USER_ID_FACTORY) private readonly serviceWorkerUserIdFactory: () => Promise<string>,
        @Inject(DISK_PRELOAD_REQUEST_MAP) private readonly diskPreloadRequestMap: ReadonlyMap<string, Promise<Response>>,
        @Inject(SERVICE_WORKER_ENABLED) private readonly serviceWorkerEnabled: boolean,
        private readonly serviceWorkerConfigService: ServiceWorkerConfigService
    ) {
        this.initialized = this.init();
    }

    /**
     * @param url The URL to try to add to the disk preload list
     */
    public async tryAddToPreloadList(url: string): Promise<void> {
        await this.initialized;

        if (!this.diskPreloadEnabled) {
            return;
        }

        const markerName = this.getMarkerName(url);

        if (!markerName || this.hasUrl(url)) {
            return;
        }

        this.updateStorageEntries([
            ...this.diskPreloadStorageEntries,
            {
                url,
                markerName
            }
        ]);
    }

    /**
     * @param urlWithParams - The full URL with query string parameters to try and read from the DiskPreload map
     * The response is preloaded in the inline script.
     */
    public async loadPreloadResponse(urlWithParams: string): Promise<Response | null | undefined> {
        await this.initialized;

        if (!this.diskPreloadEnabled) {
            return null;
        }

        return this.diskPreloadRequestMap.get(urlWithParams);
    }

    private async init(): Promise<void> {
        if (this.initialized) {
            throw new Error('`init()` can only be called once.');
        }

        if (!this.serviceWorkerConfigService.config.diskPreloadEnabled || !this.serviceWorkerEnabled) {
            this.diskPreloadEnabled = false;
            return;
        }

        const lastUserId = this.localStorageService.getGlobal(this.userIdStorageKey);
        const currentUserId = await this.serviceWorkerUserIdFactory();

        // In this case, the inline script preloaded responses from the service worker
        // for the wrong User ID. We can't use them in this application.
        if (lastUserId !== currentUserId) {
            this.diskPreloadEnabled = false;
            this.localStorageService.setGlobal(this.userIdStorageKey, currentUserId);
            this.updateStorageEntries([]);
            return;
        }

        const oldDiskPreloadList: ReadonlyArray<DiskPreloadStorageEntry> | undefined = this.localStorageService.getGlobal(this.urlPatternStorageKey);
        if (!oldDiskPreloadList) {
            return;
        }

        this.updateStorageEntries(oldDiskPreloadList.filter(entry => !!this.getMarkerName(entry.url)));
    }

    private getMarkerName(url: string): DiskPreloadMarkerName | undefined {
        try {
            return this.urlPatternEntries.find(entry => entry.pattern.test(url)).markerName;
        } catch {
            // If an incompatible schema change occurs, this will catch all exceptions,
            // and ensure the list entry is cleared on initialization.
            return undefined;
        }
    }

    private hasUrl(url: string) {
        return this.diskPreloadStorageEntries.map(entry => entry.url).indexOf(url) >= 0;
    }

    private updateStorageEntries(storageEntries: ReadonlyArray<DiskPreloadStorageEntry>) {
        this.diskPreloadStorageEntries = storageEntries;
        this.localStorageService.setGlobal(this.urlPatternStorageKey, this.diskPreloadStorageEntries);
    }
}
